import { ref } from 'vue';
import { 
  supabase, 
  safeSupabaseOperation, 
  safeAuthStateChange, 
  reinitSupabaseClient 
} from '@/lib/supabaseClient';
import emitter from '@/utils/emitter';

export function useSupabaseClient() {
  const loading = ref(false);
  const error = ref<Error | null>(null);
  
  // אתחול האזנה לאירועים של שינוי מצב אפליקציה
  if (typeof window !== 'undefined') {
    emitter.on('app:active', () => {
      console.log('[useSupabaseClient] App became active');
    });
  }

  /**
   * הרצת שאילתה עם מנגנוני הגנה (setTimeout ו-timeout)
   * למניעת תקיעות כאשר חוזרים לכרטיסיה
   */
  const executeQuery = async <T>(
    queryFn: () => Promise<{ data: T; error: any }>
  ): Promise<{ data: T | null; error: Error | null }> => {
    loading.value = true;
    error.value = null;

    try {
      // שימוש בפונקציה הבטוחה מהמודול
      const result = await safeSupabaseOperation(async () => {
        return await queryFn();
      });
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      error.value = new Error(errorMessage);
      return { data: null, error: error.value };
    } finally {
      loading.value = false;
    }
  };
  
  /**
   * הוספת התמודדות עם onAuthStateChange 
   * כדי למנוע deadlock במקרה של שינוי מצב אותנטיקציה
   * משתמש בפונקציה הבטוחה מהמודול
   */
  const safeOnAuthStateChange = (callback: (event: string, session: any) => void) => {
    return safeAuthStateChange(callback);
  };
  
  /**
   * ביצוע פעולות auth בצורה בטוחה
   * משתמש בפונקציה הבטוחה מהמודול
   */
  const safeAuthOperation = async <T>(
    operation: () => Promise<T>
  ): Promise<T> => {
    loading.value = true;
    error.value = null;
    
    try {
      const result = await safeSupabaseOperation(operation);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      error.value = new Error(errorMessage);
      throw error.value;
    } finally {
      loading.value = false;
    }
  };

  return {
    supabase,
    loading,
    error,
    executeQuery,
    safeAuthStateChange: safeOnAuthStateChange,
    safeAuthOperation,
    
    // פונקציה לאתחול מחדש של ה-client
    reinitClient: reinitSupabaseClient
  };
} 